import { MouseEvent, useEffect, useRef, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';

import { i18n } from '../config';
import type { KbTocItem } from '../lib';

import styles from './KnowledgeBase.module.scss';

/** A heading counts as the one being read once it passes the middle of the
 * scrolling pane — a fixed pixel offset lags on tall screens, and a section
 * that still fills half the pane is the section the reader is in. */
const ACTIVE_OFFSET = 0.5;

/** The nearest ancestor that actually scrolls: the panel's article pane.
 * The panel is a dialog with its own scroll container, so the window is the
 * wrong place to listen. */
const findScroller = (element: HTMLElement | null): HTMLElement | null => {
  let node = element?.parentElement ?? null;

  while (node) {
    const overflow = getComputedStyle(node).overflowY;

    if (
      (overflow === 'auto' || overflow === 'scroll') &&
      node.scrollHeight > node.clientHeight
    ) {
      return node;
    }

    node = node.parentElement;
  }

  return null;
};

const TocList = ({
  items,
  active,
  onJump,
}: {
  items: KbTocItem[];
  active: string | null;
  onJump: (event: MouseEvent<HTMLAnchorElement>, id: string) => void;
}) => (
  <ol className={styles.kbTocList}>
    {items.map((item) => {
      const isActive = item.id === active;

      return (
        <li key={item.id} className={styles.kbTocItem}>
          <a
            href={`#${item.id}`}
            className={[
              styles.kbTocLink,
              item.depth > 0 ? styles.kbTocLinkNested : '',
              isActive ? styles.kbTocLinkActive : '',
            ]
              .filter(Boolean)
              .join(' ')}
            aria-current={isActive ? 'true' : undefined}
            onClick={(event) => onJump(event, item.id)}
          >
            {item.step !== undefined && (
              <span aria-hidden="true" className={styles.kbTocStep}>
                {item.step}
              </span>
            )}
            {item.text}
          </a>
        </li>
      );
    })}
  </ol>
);

/**
 * The article contents list.
 *
 * The panel is a dialog with its own scroll container, so the outline marks
 * the heading being read inside that container, not on the page. It is a
 * sticky rail beside the article when the pane is wide enough, and it is not
 * there at all when it is not: on a narrow pane the outline competes with the
 * text for the same column. The headings themselves are the outline at that
 * size.
 */
export const KbToc = ({ items }: { items: KbTocItem[] }) => {
  const intl = useIntl();
  const navRef = useRef<HTMLElement>(null);
  const [active, setActive] = useState<string | null>(null);

  useEffect(() => {
    const scroller = findScroller(navRef.current);

    if (!scroller || items.length === 0) return;

    const headings = items
      .map((item) => document.getElementById(item.id))
      .filter((element): element is HTMLElement => element !== null);

    if (headings.length === 0) return;

    let scheduled = 0;

    const measure = () => {
      scheduled = 0;

      const bounds = scroller.getBoundingClientRect();
      const offset = bounds.top + scroller.clientHeight * ACTIVE_OFFSET;
      let current = headings[0];

      for (const heading of headings) {
        if (heading.getBoundingClientRect().top <= offset) current = heading;
      }

      setActive(current.id);
    };

    const schedule = () => {
      if (scheduled) return;

      scheduled = requestAnimationFrame(measure);
    };

    schedule();
    scroller.addEventListener('scroll', schedule, { passive: true });
    window.addEventListener('resize', schedule);
    // Lazy images land after the first measurement and move every heading
    // below them, so the outline is re-measured when the body grows.
    const body = scroller.querySelector(`.${styles.articleContent}`);
    const observer =
      body && typeof ResizeObserver !== 'undefined'
        ? new ResizeObserver(schedule)
        : null;

    if (body && observer) observer.observe(body);

    return () => {
      if (scheduled) cancelAnimationFrame(scheduled);
      scroller.removeEventListener('scroll', schedule);
      window.removeEventListener('resize', schedule);
      observer?.disconnect();
    };
  }, [items]);

  // The app owns the URL, so a contents link scrolls the pane instead of
  // writing a heading hash into the route.
  const onJump = (event: MouseEvent<HTMLAnchorElement>, id: string) => {
    const scroller = findScroller(navRef.current);
    const target = document.getElementById(id);

    if (!scroller || !target) return;

    event.preventDefault();

    const delta =
      target.getBoundingClientRect().top - scroller.getBoundingClientRect().top;

    if (typeof scroller.scrollTo === 'function') {
      scroller.scrollTo({
        top: scroller.scrollTop + delta - 16,
        behavior: 'smooth',
      });
    }

    setActive(id);
  };

  if (items.length === 0) return null;

  return (
    <nav
      ref={navRef}
      className={styles.kbToc}
      aria-label={intl.formatMessage(i18n.contents)}
      data-component="knowledge-base-toc"
    >
      <p className={styles.kbTocTitle}>
        <FormattedMessage {...i18n.contents} />
      </p>
      <TocList items={items} active={active} onJump={onJump} />
    </nav>
  );
};
